import React, { useEffect, useState, useRef } from 'react';
import { Layout } from '../components/Layout';
import { SEO, pageSEO } from '../components/SEO';
import { Card, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { CreditCard, Wallet, Building2, Heart, Briefcase, Users, TrendingUp, Loader2, CheckCircle2 } from 'lucide-react';
import { stripeService } from '../services/stripeService';
import { paypalService } from '../services/paypalService';
export function DonatePage() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    companyName: '',
    email: '',
    amount: '',
    currency: 'EUR'
  });
  const [paymentMethod, setPaymentMethod] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [paypalLoading, setPaypalLoading] = useState(false);
  const [paypalReady, setPaypalReady] = useState(false);
  const paypalContainerRef = useRef<HTMLDivElement>(null);
  // Load PayPal hosted button when PayPal is selected
  useEffect(() => {
    if (paymentMethod === 'paypal' && !paypalReady) {
      setPaypalLoading(true);
      paypalService.renderHostedButton('paypal-button-container').then(success => {
        setPaypalLoading(false);
        setPaypalReady(success);
        if (!success) {
          console.log('PayPal hosted button not available, will use redirect fallback');
        }
      });
    }
  }, [paymentMethod]);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Manual validation with clear messages
    if (!formData.firstName.trim()) {
      alert('Veuillez entrer votre prénom.');
      return;
    }
    if (!formData.lastName.trim()) {
      alert('Veuillez entrer votre nom.');
      return;
    }
    if (!formData.email.trim() || !formData.email.includes('@')) {
      alert('Veuillez entrer une adresse email valide.');
      return;
    }
    if (!formData.amount) {
      alert('Veuillez entrer un montant.');
      return;
    }
    if (!paymentMethod) {
      alert('Veuillez sélectionner un moyen de paiement.');
      return;
    }
    setIsProcessing(true);
    try {
      if (paymentMethod === 'card') {
        const result = await stripeService.createCheckoutSession({
          amount: Number(formData.amount),
          currency: formData.currency,
          donorInfo: {
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            companyName: formData.companyName
          }
        });
        if (!result.success) {
          throw new Error(result.error);
        }
      } else if (paymentMethod === 'paypal') {
        // If the hosted button is ready, we don't need to do anything here
        // as the button handles the transaction directly
        if (!paypalReady) {
          // Fallback to standard redirect if hosted button failed to load
          const result = await paypalService.createOrder({
            amount: Number(formData.amount),
            currency: formData.currency,
            donorInfo: {
              firstName: formData.firstName,
              lastName: formData.lastName,
              email: formData.email
            }
          });
          if (!result.success) {
            throw new Error(result.error);
          }
        } else {
          // The user should click the PayPal button that was rendered
          setIsProcessing(false);
          return;
        }
      }
    } catch (error) {
      console.error('Payment error:', error);
      alert('Une erreur est survenue lors du traitement de votre don. Veuillez réessayer.');
      setIsProcessing(false);
    }
  };
  return <Layout data-id="element-864">
      <SEO {...pageSEO.donate} data-id="element-865" />
      <div className="bg-[#2C3E50] text-white py-16" data-id="element-866">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-867">
          <span className="inline-block bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1 text-sm font-medium text-blue-200 mb-4" data-id="element-868">
            Faire un don
          </span>
          <h1 className="text-4xl font-bold mb-4" data-id="element-869">Soutenez l'ADCC</h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto" data-id="element-870">
            Votre contribution permet de financer nos actions de formation et
            d'accompagnement vers l'emploi digne au Bénin.
          </p>
        </div>
      </div>

      <div className="bg-gradient-to-b from-[#2C3E50] to-white pb-12" data-id="element-871">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-872">
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 text-white text-center border border-white/20" data-id="element-873">
            <h2 className="text-2xl font-bold mb-2" data-id="element-874">Votre impact concret</h2>
            <p className="text-lg font-semibold" data-id="element-875">
              Soutenez l'insertion professionnelle durable.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-876">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12" data-id="element-877">
          <Card className="bg-blue-50 border-blue-100" data-id="element-878">
            <CardContent className="p-6 text-center" data-id="element-879">
              <Users className="w-8 h-8 text-[#1E88E5] mx-auto mb-4" data-id="element-880" />
              <h3 className="font-bold text-gray-900 mb-2" data-id="element-881">Développer</h3>
              <p className="text-sm text-gray-600" data-id="element-882">
                Financer des ateliers de renforcement de capacités de qualité.
              </p>
            </CardContent>
          </Card>
          <Card className="bg-blue-50 border-blue-100" data-id="element-883">
            <CardContent className="p-6 text-center" data-id="element-884">
              <Briefcase className="w-8 h-8 text-[#1E88E5] mx-auto mb-4" data-id="element-885" />
              <h3 className="font-bold text-gray-900 mb-2" data-id="element-886">Insérer</h3>
              <p className="text-sm text-gray-600" data-id="element-887">
                Soutenir les dispositifs de placement et mise en relation avec
                employeurs.
              </p>
            </CardContent>
          </Card>
          <Card className="bg-blue-50 border-blue-100" data-id="element-888">
            <CardContent className="p-6 text-center" data-id="element-889">
              <TrendingUp className="w-8 h-8 text-[#1E88E5] mx-auto mb-4" data-id="element-890" />
              <h3 className="font-bold text-gray-900 mb-2" data-id="element-891">Suivre</h3>
              <p className="text-sm text-gray-600" data-id="element-892">
                Accompagner les bénéficiaires après leur placement pour garantir
                la durabilité.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Impact Statement */}
        <div className="bg-gradient-to-r from-[#1E88E5] to-[#1976D2] rounded-2xl p-8 text-white mb-12" data-id="element-893">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center" data-id="element-894">
            <div data-id="element-895">
              <p className="text-3xl font-bold mb-2" data-id="element-896">50€</p>
              <p className="text-sm text-blue-100" data-id="element-897">
                Finance le matériel pédagogique pour 2 bénéficiaires
              </p>
            </div>
            <div className="border-t md:border-t-0 md:border-l border-white/20 pt-6 md:pt-0" data-id="element-898">
              <p className="text-3xl font-bold mb-2" data-id="element-899">150€</p>
              <p className="text-sm text-blue-100" data-id="element-900">
                Forme et place 1 bénéficiaire
              </p>
            </div>
            <div className="border-t md:border-t-0 md:border-l border-white/20 pt-6 md:pt-0" data-id="element-901">
              <p className="text-3xl font-bold mb-2" data-id="element-902">500€</p>
              <p className="text-sm text-blue-100" data-id="element-903">
                Soutient le dispositif de placement pendant 1 mois
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-904">
          <div className="lg:col-span-2" data-id="element-905">
            <Card data-id="element-906">
              <CardContent className="p-8" data-id="element-907">
                <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-908">
                  Informations du donateur
                </h2>
                <form onSubmit={handleSubmit} className="space-y-6" data-id="element-909">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-910">
                    <div data-id="element-911">
                      <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-912">
                        Prénom *
                      </label>
                      <input type="text" id="firstName" name="firstName" value={formData.firstName} onChange={handleInputChange} required disabled={isProcessing} className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#1E88E5] focus:ring-[#1E88E5] p-2 border disabled:opacity-50" data-id="element-913" />
                    </div>
                    <div data-id="element-914">
                      <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-915">
                        Nom *
                      </label>
                      <input type="text" id="lastName" name="lastName" value={formData.lastName} onChange={handleInputChange} required disabled={isProcessing} className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#1E88E5] focus:ring-[#1E88E5] p-2 border disabled:opacity-50" data-id="element-916" />
                    </div>
                  </div>

                  <div data-id="element-917">
                    <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-918">
                      Entreprise (optionnel)
                    </label>
                    <div className="relative" data-id="element-919">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-920">
                        <Building2 className="h-5 w-5 text-gray-400" data-id="element-921" />
                      </div>
                      <input type="text" id="companyName" name="companyName" value={formData.companyName} onChange={handleInputChange} disabled={isProcessing} className="w-full pl-10 rounded-md border-gray-300 shadow-sm focus:border-[#1E88E5] focus:ring-[#1E88E5] p-2 border disabled:opacity-50" data-id="element-922" />
                    </div>
                  </div>

                  <div data-id="element-923">
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-924">
                      Email *
                    </label>
                    <input type="email" id="email" name="email" value={formData.email} onChange={handleInputChange} required disabled={isProcessing} className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#1E88E5] focus:ring-[#1E88E5] p-2 border disabled:opacity-50" data-id="element-925" />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-926">
                    <div data-id="element-927">
                      <label htmlFor="amount" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-928">
                        Montant du don *
                      </label>
                      <div className="relative" data-id="element-929">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-930">
                          <span className="text-gray-500 sm:text-sm" data-id="element-931">
                            {formData.currency === 'EUR' ? '€' : formData.currency === 'XOF' ? 'CFA' : '$'}
                          </span>
                        </div>
                        <input type="number" id="amount" name="amount" min="1" step="1" value={formData.amount} onChange={handleInputChange} required disabled={isProcessing} className="w-full pl-8 rounded-md border-gray-300 shadow-sm focus:border-[#1E88E5] focus:ring-[#1E88E5] p-2 border disabled:opacity-50" placeholder="0.00" data-id="element-932" />
                      </div>
                    </div>
                    <div data-id="element-933">
                      <label htmlFor="currency" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-934">
                        Devise
                      </label>
                      <select id="currency" name="currency" value={formData.currency} onChange={handleInputChange} disabled={isProcessing} className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#1E88E5] focus:ring-[#1E88E5] p-2 border disabled:opacity-50 bg-white" data-id="element-935">
                        <option value="EUR" data-id="element-936">EUR (€)</option>
                        <option value="XOF" data-id="element-937">XOF (FCFA)</option>
                        <option value="USD" data-id="element-938">USD ($)</option>
                      </select>
                    </div>
                  </div>

                  <div data-id="element-939">
                    <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-940">
                      Moyen de paiement *
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-941">
                      <label className={`relative flex cursor-pointer rounded-lg border bg-white p-4 shadow-sm focus:outline-none ${paymentMethod === 'card' ? 'border-[#1E88E5] ring-1 ring-[#1E88E5]' : 'border-gray-300'} ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-942">
                        <input type="radio" name="paymentMethod" value="card" className="sr-only" onChange={e => setPaymentMethod(e.target.value)} disabled={isProcessing} data-id="element-943" />
                        <span className="flex flex-1" data-id="element-944">
                          <span className="flex flex-col" data-id="element-945">
                            <span className="block text-sm font-medium text-gray-900" data-id="element-946">
                              Carte Bancaire
                            </span>
                            <span className="mt-1 flex items-center text-sm text-gray-500" data-id="element-947">
                              <CreditCard className="w-4 h-4 mr-2" data-id="element-948" />
                              Stripe
                            </span>
                          </span>
                        </span>
                        <CheckCircle2 className={`h-5 w-5 text-[#1E88E5] ${paymentMethod === 'card' ? 'block' : 'hidden'}`} data-id="element-949" />
                      </label>

                      <label className={`relative flex cursor-pointer rounded-lg border bg-white p-4 shadow-sm focus:outline-none ${paymentMethod === 'paypal' ? 'border-[#1E88E5] ring-1 ring-[#1E88E5]' : 'border-gray-300'} ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-950">
                        <input type="radio" name="paymentMethod" value="paypal" className="sr-only" onChange={e => setPaymentMethod(e.target.value)} disabled={isProcessing} data-id="element-951" />
                        <span className="flex flex-1" data-id="element-952">
                          <span className="flex flex-col" data-id="element-953">
                            <span className="block text-sm font-medium text-gray-900" data-id="element-954">
                              PayPal
                            </span>
                            <span className="mt-1 flex items-center text-sm text-gray-500" data-id="element-955">
                              <Wallet className="w-4 h-4 mr-2" data-id="element-956" />
                              PayPal
                            </span>
                          </span>
                        </span>
                        <CheckCircle2 className={`h-5 w-5 text-[#1E88E5] ${paymentMethod === 'paypal' ? 'block' : 'hidden'}`} data-id="element-957" />
                      </label>
                    </div>
                  </div>

                  {/* PayPal Hosted Button Container */}
                  {paymentMethod === 'paypal' && <div className="mt-6" data-id="element-958">
                      {paypalLoading ? <div className="flex items-center justify-center p-4 bg-gray-50 rounded-lg border border-gray-200" data-id="element-959">
                          <Loader2 className="w-6 h-6 text-[#1E88E5] animate-spin mr-2" data-id="element-960" />
                          <span className="text-gray-600" data-id="element-961">
                            Chargement de PayPal...
                          </span>
                        </div> : paypalReady ? <div id="paypal-button-container" ref={paypalContainerRef} className="min-h-[150px] w-full" data-id="element-962"></div> : null}
                    </div>}

                  {(!paymentMethod || paymentMethod === 'card') && <Button type="submit" className="w-full text-lg py-6" disabled={isProcessing || !paymentMethod} data-id="element-963">
                      {isProcessing ? <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" data-id="element-964" />
                          Traitement en cours...
                        </> : 'Valider mon don'}
                    </Button>}
                </form>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6" data-id="element-965">
            <Card className="bg-gray-50 border-none" data-id="element-966">
              <CardContent className="p-6" data-id="element-967">
                <h3 className="font-bold text-gray-900 mb-4" data-id="element-968">
                  Paiement sécurisé
                </h3>
                <p className="text-sm text-gray-600 mb-4" data-id="element-969">
                  Vos dons sont traités de manière sécurisée par nos partenaires
                  de paiement certifiés.
                </p>
                <div className="flex gap-4" data-id="element-970">
                  <div className="flex-1 bg-white p-3 rounded border text-center" data-id="element-971">
                    <span className="font-bold text-[#635BFF]" data-id="element-972">stripe</span>
                  </div>
                  <div className="flex-1 bg-white p-3 rounded border text-center" data-id="element-973">
                    <span className="font-bold text-[#00457C]" data-id="element-974">PayPal</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-blue-50 border-none" data-id="element-975">
              <CardContent className="p-6" data-id="element-976">
                <h3 className="font-bold text-gray-900 mb-2" data-id="element-977">
                  Déduction fiscale
                </h3>
                <p className="text-sm text-gray-600" data-id="element-978">
                  L'ADCC étant une association reconnue, vos dons peuvent ouvrir
                  droit à une déduction fiscale selon la législation en vigueur
                  dans votre pays de résidence. Un reçu fiscal vous sera envoyé
                  automatiquement.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>;
}